/**
 * Copy and rules shared by sign in, forgot password and reset password.
 * Pure; the pages and `authApi.ts` call it.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isEmail = (v: string) => EMAIL_RE.test(v.trim());

/** Client-side email check; `undefined` when the value is fine. */
export function emailError(v: string): string | undefined {
  if (!v.trim()) return 'Enter your email.';
  if (!isEmail(v)) return 'Enter an email like name@restaurant.com.';
  return undefined;
}

/**
 * Rules the client can check. The backend runs Django's validators too
 * (common passwords, similarity to the email); those come back as
 * `non_field_errors` and are shown as-is above the button.
 */
export const PASSWORD_RULES: {
  key: string;
  label: string;
  test: (p: string) => boolean;
}[] = [
  {
    key: 'length',
    label: 'At least 8 characters',
    test: (p) => p.length >= 8,
  },
  {
    key: 'notNumeric',
    label: 'Not only numbers',
    test: (p) => p.length > 0 && !/^\d+$/.test(p),
  },
];

export const passwordMeetsRules = (p: string) =>
  PASSWORD_RULES.every((r) => r.test(p));

/** One sentence per way a request can fail. Never say which half was wrong. */
export const SIGN_IN_ERROR = {
  credentials:
    'That email and password don\u2019t match. Check both and try again.',
  network: 'Can\u2019t reach AskArthur. Check your connection and try again.',
  server: 'Something went wrong on our side. Try again in a minute.',
} as const;

/** Map `useAuth().login`'s messages onto the three sentences we say. */
export function classifySignInError(message: string | undefined): string {
  const m = (message ?? '').toLowerCase();
  if (!m || m.includes('failed to fetch') || m.includes('network'))
    return SIGN_IN_ERROR.network;
  if (
    m.includes('credential') ||
    m.includes('password') ||
    m.includes('no active account')
  )
    return SIGN_IN_ERROR.credentials;
  return SIGN_IN_ERROR.server;
}

/** Router-state notice the reset flow hands to sign in. */
export const PASSWORD_CHANGED_NOTICE =
  'Your password was changed. Sign in with the new one.';
